import { twMerge } from 'tailwind-merge';

type LoadingProps = {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  containerClassName?: string;
};

const sizeMap = {
  xs: 'w-4 h-4 p-[2px]',
  sm: 'w-6 h-6 p-[3px]',
  md: 'w-8 h-8 p-[4px]',
  lg: 'w-10 h-10 p-[5px]',
  xl: 'w-12 h-12 p-[6px]',
};

const borderSizeMap = {
  xs: 'border-[1.25px]',
  sm: 'border-[2px]',
  md: 'border-[2.5px]',
  lg: 'border-[3.5px]',
  xl: 'border-[4px]',
};

export function Loading(props: LoadingProps) {
  const { size = 'sm', className } = props;
  const contentClasses = twMerge([
    'animate-spin rounded-full w-full h-full border-branding-accent-600 border-t-transparent',
    borderSizeMap[size],
    className,
  ]);

  return (
    <div className={twMerge(sizeMap[size], props.containerClassName ?? '')}>
      <div className={contentClasses} />
    </div>
  );
}

export function BlymLoading(props: { className?: string }) {
  const { className } = props;
  return (
    <svg
      width="41"
      height="37"
      viewBox="0 0 41 37"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes fill-1 {
            0%, 19%, 81%, 100% { fill: #F5F5F5; }
            20%, 80% { fill: #535CDC; }
          }

          @keyframes fill-2 {
            0%, 36.5%, 81%, 100% { fill: #F5F5F5; }
            37.5%, 80% { fill: #535CDC; }
          }

          @keyframes fill-3 {
            0%, 49%, 81%, 100% { fill: #F5F5F5; }
            50%, 80% { fill: #535CDC; }
          }

          @keyframes fill-4 {
            0%, 61.5%, 81%, 100% { fill: #F5F5F5; }
            62.5%, 80% { fill: #535CDC; }
          }

          .path-1 { animation: fill-1 1.5s linear infinite; }
          .path-2 { animation: fill-2 1.5s linear infinite; }
          .path-3 { animation: fill-3 1.5s linear infinite; }
          .path-4 { animation: fill-4 1.5s linear infinite; }
        `}
      </style>
      <g clipPath="url(#clip0_2297_347)">
        <path
          className="path-4"
          d="M39.2064 21.6425L32.517 33.2516C31.3216 35.3232 29.1192 36.5921 26.7402 36.5921H13.3613C10.9823 36.5921 8.77994 35.3232 7.58455 33.2516L7.51978 33.1395C7.82598 32.5788 8.1793 32.024 8.57384 31.4751C9.18037 30.6252 9.89289 29.7813 10.7173 28.955C11.4416 28.2291 12.1895 27.6034 12.9491 27.0841C18.7612 23.0412 25.115 24.8 28.3773 26.6473C26.54 23.3717 24.7852 17.0094 28.813 11.1842C29.3371 10.4169 29.9554 9.6733 30.6856 8.94146C31.5041 8.12109 32.3403 7.40695 33.1942 6.79905C33.5121 6.57478 33.8242 6.3564 34.1422 6.16754L39.2064 14.9556C40.396 17.0271 40.396 19.5709 39.2064 21.6425Z"
          fill="#F5F5F5"
        />
        <path
          className="path-2"
          d="M32.5816 3.45855C32.2754 4.01923 31.9221 4.57992 31.5276 5.1288C30.921 5.97868 30.2085 6.81676 29.3841 7.64303C28.6598 8.36897 27.9119 8.99458 27.1523 9.51395C21.3344 13.5568 14.9923 11.8039 11.7241 9.9507C13.5673 13.2263 15.3162 19.5886 11.2884 25.4138C10.7643 26.1811 10.146 26.9247 9.41578 27.6566C8.59727 28.4769 7.75519 29.197 6.90723 29.8049C6.59513 30.0291 6.27715 30.2416 5.96505 30.4364L0.894949 21.6425C-0.294553 19.5709 -0.294553 17.0271 0.894949 14.9556L4.23969 9.15098L7.58442 3.34641C8.77981 1.27482 10.9822 0 13.3553 0H26.7342C29.1132 0 31.3156 1.27482 32.511 3.34641L32.5757 3.45855H32.5816Z"
          fill="#F5F5F5"
        />
        <path
          className="path-1"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.3525 0.374283H13.3524C12.7592 0.374283 12.1766 0.453959 11.6177 0.605842C12.1766 0.453957 12.7592 0.374283 13.3525 0.374283ZM9.65764 1.50002C8.81968 2.06122 8.10437 2.81477 7.58161 3.72069L6.07059 6.34298L7.5816 3.72069C8.10435 2.81477 8.81968 2.06122 9.65764 1.50002ZM11.6893 10.1075C11.679 10.1661 11.6892 10.2381 11.7213 10.325C13.5644 13.6006 15.3134 19.9629 11.2855 25.7881C10.7615 26.5554 10.1432 27.299 9.41296 28.0308C8.59444 28.8512 7.75237 29.5713 6.90441 30.1792C6.59231 30.4034 6.27433 30.6159 5.96223 30.8107L0.892126 22.0168C-0.297375 19.9452 -0.297375 17.4014 0.892126 15.3298L4.23686 9.52526L6.07059 6.34298L11.6893 10.1075Z"
          fill="#F5F5F5"
        />
        <path
          className="path-3"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M26.3792 36.4364L26.3792 36.4364C26.9725 36.4364 27.5551 36.3567 28.1139 36.2048C27.5551 36.3567 26.9725 36.4364 26.3792 36.4364ZM30.074 35.3107C30.912 34.7495 31.6273 33.9959 32.15 33.09L33.6611 30.4677L32.15 33.09C31.6273 33.9959 30.912 34.7495 30.074 35.3107ZM26.6477 25.8066C27.1453 26.024 27.6018 26.254 28.0103 26.4857C26.1672 23.2101 24.4183 16.8478 28.4461 11.0226C28.9702 10.2553 29.5885 9.51167 30.3187 8.77982C31.1372 7.95945 31.9793 7.23941 32.8272 6.63151C33.1393 6.40724 33.4573 6.19477 33.7694 6L38.8395 14.7939C40.029 16.8655 40.029 19.4092 38.8395 21.4808L35.4948 27.2854L33.6611 30.4677L26.8146 25.8806C26.7595 25.8558 26.7039 25.8311 26.6477 25.8066Z"
          fill="#F5F5F5"
        />
      </g>
      <defs>
        <clipPath id="clip0_2297_347">
          <rect width="40.0956" height="36.5921" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
